import React, { createContext, useContext, useEffect, useRef } from 'react';
import useUser from 'hooks/useUser';
import { isHeadless } from 'utils/headless';
import { MarketLive, useMarketLiveSubscription } from 'generated/graphql';

// graphql-ws reconnects dropped sockets on its own (retryAttempts: Infinity), so this
// retry only covers the closes it treats as fatal. Those tend to keep failing for the
// same reason, so back off instead of resubscribing as fast as the errors arrive.
const RETRY_BASE_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 30000;

export const GlobalSubscriptionsContext = createContext<
  | {
      marketLive?: MarketLive;
      loading: boolean;
      error?: Error;
    }
  | undefined
>(undefined);

export const GlobalSubscriptionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isInitialised } = useUser();
  const [result, retry] = useMarketLiveSubscription({
    pause: !user?.email || !isInitialised || isHeadless(),
  });

  const { data, fetching, error } = result;

  const consecutiveFailures = useRef(0);

  useEffect(() => {
    if (data) {
      consecutiveFailures.current = 0;
    }
  }, [data]);

  useEffect(() => {
    if (!error?.networkError) return;

    const delay = Math.min(RETRY_BASE_DELAY_MS * 2 ** consecutiveFailures.current, RETRY_MAX_DELAY_MS);
    consecutiveFailures.current += 1;

    const timeout = setTimeout(retry, delay);
    return () => clearTimeout(timeout);
  }, [error, retry]);

  const value = {
    marketLive: data?.marketLive,
    loading: fetching,
    error: error?.networkError,
  };

  return <GlobalSubscriptionsContext.Provider value={value}>{children}</GlobalSubscriptionsContext.Provider>;
};

export const useGlobalSubscriptions = () => {
  const context = useContext(GlobalSubscriptionsContext);
  if (!context) {
    throw new Error('useGlobalSubscriptions must be used within a GlobalSubscriptionsProvider');
  }
  return context;
};
